import type { IDBPTransaction } from 'idb'

import {
  criarChaveRegistroCatalogo,
  criarHashContextoCatalogo,
} from '../catalogo.chave'
import {
  IDADE_MAXIMA_CATALOGO_MS,
  LIMITE_CONTEXTOS_CATALOGO,
  classificarRegistroCatalogo,
} from '../catalogo.politica'
import type {
  CatalogoDescriptor,
  CatalogosPersistidos,
  ContextoDeCatalogo,
  ResultadoLeituraCatalogo,
} from '../catalogo.tipos'
import {
  IDENTIFICADOR_CHAVE_CATALOGOS,
  abrirBancoCatalogos,
  type BancoCatalogos,
  type BancoCatalogosSchema,
  type RegistroCatalogoPersistido,
} from './banco-catalogos'
import {
  descriptografarItensCatalogo,
  ehChaveCatalogosValida,
  gerarChaveCatalogos,
  criptografarItensCatalogo,
} from './criptografia-catalogos'

type TransacaoCatalogosEContextos = IDBPTransaction<
  BancoCatalogosSchema,
  ['catalogos', 'contextos'],
  'readwrite'
>

type ConfiguracaoCatalogosIndexedDb = Readonly<{
  agora?: () => number
  abrirBanco?: () => Promise<BancoCatalogos>
  criptografia?: Crypto | null
}>

const removerContextosExcedentes = async (
  transacao: TransacaoCatalogosEContextos,
): Promise<void> => {
  const contextos = await transacao.objectStore('contextos').getAll()
  const excedentes = contextos
    .sort((a, b) => (
      a.ultimoAcessoEm - b.ultimoAcessoEm
      || a.contextoHash.localeCompare(b.contextoHash)
    ))
    .slice(0, Math.max(0, contextos.length - LIMITE_CONTEXTOS_CATALOGO))

  const catalogos = transacao.objectStore('catalogos')
  const indiceContexto = catalogos.index('por-contexto')
  for (const contexto of excedentes) {
    const chaves = await indiceContexto.getAllKeys(contexto.contextoHash)
    await Promise.all(chaves.map((chave) => catalogos.delete(chave)))
    await transacao.objectStore('contextos').delete(contexto.contextoHash)
  }
}

const removerRegistrosExpiradosNaTransacao = async (
  transacao: TransacaoCatalogosEContextos,
  instanteAtual: number,
): Promise<void> => {
  const storeCatalogos = transacao.objectStore('catalogos')
  const registros = await storeCatalogos.getAll()
  const registrosValidos = registros.filter((registro) => (
    instanteAtual - registro.sincronizadoEm <= IDADE_MAXIMA_CATALOGO_MS
  ))
  const contextosComRegistrosValidos = new Set(
    registrosValidos.map(({ contextoHash }) => contextoHash),
  )

  await Promise.all(registros
    .filter((registro) => (
      instanteAtual - registro.sincronizadoEm > IDADE_MAXIMA_CATALOGO_MS
    ))
    .map(({ chave }) => storeCatalogos.delete(chave)))

  const storeContextos = transacao.objectStore('contextos')
  const contextos = await storeContextos.getAll()
  await Promise.all(contextos
    .filter(({ contextoHash }) => (
      !contextosComRegistrosValidos.has(contextoHash)
    ))
    .map(({ contextoHash }) => storeContextos.delete(contextoHash)))
}

const registrarContextoNaTransacao = async (
  transacao: TransacaoCatalogosEContextos,
  contextoHash: string,
  ultimoAcessoEm: number,
): Promise<void> => {
  await transacao.objectStore('contextos').put({
    contextoHash,
    ultimoAcessoEm,
  })
  await removerRegistrosExpiradosNaTransacao(transacao, ultimoAcessoEm)
  await removerContextosExcedentes(transacao)
}

const ehRegistroCompativel = <T>(
  registro: RegistroCatalogoPersistido,
  catalogo: CatalogoDescriptor<T>,
  contextoHash: string,
): boolean => (
  registro.catalogo === catalogo.nome
  && registro.contextoHash === contextoHash
  && registro.versaoProjecao === catalogo.versaoProjecao
  && registro.iv instanceof Uint8Array
  && registro.conteudoCifrado instanceof ArrayBuffer
)

const removerRegistroInvalido = async (
  banco: BancoCatalogos,
  chave: string,
  instanteAtual: number,
): Promise<void> => {
  const transacao = banco.transaction(
    ['catalogos', 'contextos'],
    'readwrite',
  )
  await transacao.objectStore('catalogos').delete(chave)
  await removerRegistrosExpiradosNaTransacao(transacao, instanteAtual)
  await transacao.done
}

export const criarCatalogosIndexedDb = (
  configuracao: ConfiguracaoCatalogosIndexedDb = {},
): CatalogosPersistidos => {
  const agora = configuracao.agora ?? Date.now
  const criarConexao = configuracao.abrirBanco ?? abrirBancoCatalogos
  const criptografia = configuracao.criptografia === undefined
    ? globalThis.crypto
    : configuracao.criptografia
  let promessaBanco: Promise<BancoCatalogos> | undefined
  let promessaChave: Promise<CryptoKey> | undefined

  const abrirBanco = (): Promise<BancoCatalogos> => {
    promessaBanco ??= criarConexao().catch((erro: unknown) => {
      promessaBanco = undefined
      throw erro
    })
    return promessaBanco
  }

  const obterOuCriarChave = async (): Promise<CryptoKey> => {
    if (!criptografia) throw new Error('Web Crypto indisponivel')
    const banco = await abrirBanco()
    const persistida = await banco.get(
      'chaves',
      IDENTIFICADOR_CHAVE_CATALOGOS,
    )
    if (persistida) {
      if (!ehChaveCatalogosValida(persistida.chave)) {
        throw new Error('Chave dos catalogos invalida')
      }
      return persistida.chave
    }

    if (await banco.count('catalogos') > 0) {
      throw new Error('Chave dos catalogos ausente')
    }

    const novaChave = await gerarChaveCatalogos(criptografia)
    try {
      await banco.add('chaves', {
        id: IDENTIFICADOR_CHAVE_CATALOGOS,
        chave: novaChave,
      })
      return novaChave
    } catch (erro: unknown) {
      const criadaConcorrentemente = await banco.get(
        'chaves',
        IDENTIFICADOR_CHAVE_CATALOGOS,
      )
      if (
        criadaConcorrentemente
        && ehChaveCatalogosValida(criadaConcorrentemente.chave)
      ) return criadaConcorrentemente.chave
      throw erro
    }
  }

  const obterChave = (): Promise<CryptoKey> => {
    promessaChave ??= obterOuCriarChave().catch((erro: unknown) => {
      promessaChave = undefined
      throw erro
    })
    return promessaChave
  }

  const registrarAcessoAoContexto = async (
    contexto: ContextoDeCatalogo,
  ): Promise<void> => {
    try {
      const banco = await abrirBanco()
      const transacao = banco.transaction(
        ['catalogos', 'contextos'],
        'readwrite',
      )
      await registrarContextoNaTransacao(
        transacao,
        criarHashContextoCatalogo(contexto),
        agora(),
      )
      await transacao.done
    } catch {
      // IndexedDB e cache best-effort.
    }
  }

  const ler = async <T>(
    catalogo: CatalogoDescriptor<T>,
    contexto: ContextoDeCatalogo,
  ): Promise<ResultadoLeituraCatalogo<T>> => {
    try {
      const banco = await abrirBanco()
      const chaveRegistro = criarChaveRegistroCatalogo(
        catalogo.nome,
        contexto,
      )
      const contextoHash = criarHashContextoCatalogo(contexto)
      const instanteAtual = agora()
      const registro = await banco.get('catalogos', chaveRegistro)
      if (!registro) return { estado: 'ausente' }

      if (!ehRegistroCompativel(registro, catalogo, contextoHash)) {
        await removerRegistroInvalido(banco, chaveRegistro, instanteAtual)
        return { estado: 'ausente' }
      }

      const classificacao = classificarRegistroCatalogo(
        registro,
        instanteAtual,
      )
      if (classificacao === 'expirado') {
        const transacao = banco.transaction(
          ['catalogos', 'contextos'],
          'readwrite',
        )
        await removerRegistrosExpiradosNaTransacao(
          transacao,
          instanteAtual,
        )
        await transacao.done
        return { estado: 'expirado' }
      }

      if (!criptografia) return { estado: 'indisponivel' }
      const chaveCriptografica = await obterChave()
      let itens: unknown
      try {
        itens = await descriptografarItensCatalogo(
          criptografia,
          chaveCriptografica,
          registro,
        )
        if (!catalogo.validarItens(itens)) {
          throw new Error('Itens descriptografados invalidos')
        }
      } catch {
        await removerRegistroInvalido(banco, chaveRegistro, instanteAtual)
        return { estado: 'ausente' }
      }

      const transacao = banco.transaction(
        ['catalogos', 'contextos'],
        'readwrite',
      )
      await registrarContextoNaTransacao(
        transacao,
        contextoHash,
        instanteAtual,
      )
      await transacao.done
      return {
        estado: classificacao,
        itens,
        sincronizadoEm: registro.sincronizadoEm,
      }
    } catch {
      return { estado: 'indisponivel' }
    }
  }

  const salvar = async <T>(
    catalogo: CatalogoDescriptor<T>,
    contexto: ContextoDeCatalogo,
    itens: readonly T[],
    sincronizadoEm = agora(),
  ): Promise<void> => {
    if (!catalogo.validarItens(itens)) return

    try {
      if (!criptografia) throw new Error('Web Crypto indisponivel')
      const banco = await abrirBanco()
      const contextoHash = criarHashContextoCatalogo(contexto)
      const ultimoAcessoEm = agora()
      const metadados = {
        chave: criarChaveRegistroCatalogo(catalogo.nome, contexto),
        catalogo: catalogo.nome,
        contextoHash,
        versaoProjecao: catalogo.versaoProjecao,
        sincronizadoEm,
        ultimoAcessoEm,
      }
      const conteudo = await criptografarItensCatalogo(
        criptografia,
        await obterChave(),
        metadados,
        itens,
      )
      const transacao = banco.transaction(
        ['catalogos', 'contextos'],
        'readwrite',
      )
      await transacao.objectStore('catalogos').put({
        ...metadados,
        ...conteudo,
      })
      await registrarContextoNaTransacao(
        transacao,
        contextoHash,
        ultimoAcessoEm,
      )
      await transacao.done
    } catch {
      // IndexedDB e cache best-effort.
    }
  }

  const invalidar = async <T>(
    catalogo: CatalogoDescriptor<T>,
    contexto: ContextoDeCatalogo,
  ): Promise<void> => {
    try {
      const banco = await abrirBanco()
      const chave = criarChaveRegistroCatalogo(catalogo.nome, contexto)
      const transacao = banco.transaction('catalogos', 'readwrite')
      const catalogos = transacao.objectStore('catalogos')
      const registro = await catalogos.get(chave)
      if (!registro) {
        await transacao.done
        return
      }
      await catalogos.put({
        ...registro,
        invalidadoEm: agora(),
      })
      await transacao.done
    } catch {
      // IndexedDB e cache best-effort.
    }
  }

  const removerExpirados = async (): Promise<void> => {
    try {
      const banco = await abrirBanco()
      const instanteAtual = agora()
      const transacao = banco.transaction(
        ['catalogos', 'contextos'],
        'readwrite',
      )
      await removerRegistrosExpiradosNaTransacao(transacao, instanteAtual)
      await transacao.done
    } catch {
      // IndexedDB e cache best-effort.
    }
  }

  return {
    ler,
    salvar,
    invalidar,
    removerExpirados,
    registrarAcessoAoContexto,
  }
}
